/**
 * DriverShareCodePanel
 *
 * Modal panel shown to drivers from their profile menu. Displays:
 *   - Their unique share code (with copy + regenerate)
 *   - Pending company link requests (approve / reject)
 *   - Active company links (revoke)
 *
 * The code lets OTHER companies add this driver to their roster.
 * The driver approves each request before the other company can dispatch them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Copy, Check, RefreshCw, ShieldCheck, ShieldX, Link2, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'Pending', cls: 'bg-amber-900/40 text-amber-300 border-amber-800/50' },
    active: { label: 'Active', cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-800/50' },
    rejected: { label: 'Rejected', cls: 'bg-slate-800 text-slate-400 border-slate-700' },
    revoked: { label: 'Revoked', cls: 'bg-rose-900/40 text-rose-300 border-rose-800/50' },
  };
  const m = map[status] || map.pending;
  return <Badge className={`${m.cls} border`}>{m.label}</Badge>;
};

export const DriverShareCodePanel = ({ open, onOpenChange }) => {
  const { api } = useAuth();
  const [code, setCode] = useState('');
  const [codeGeneratedAt, setCodeGeneratedAt] = useState('');
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actingLink, setActingLink] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [codeRes, linksRes] = await Promise.all([
        api.get('/driver-share/me/code'),
        api.get('/driver-share/me/links'),
      ]);
      setCode(codeRes.data.share_code);
      setCodeGeneratedAt(codeRes.data.share_code_generated_at);
      setLinks(linksRes.data.links || []);
    } catch (err) {
      toast.error('Failed to load share code');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Code copied');
    } catch {
      toast.error('Copy failed — select the code manually');
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate will invalidate your old code. Companies you already linked with will stay linked. Continue?')) return;
    setRegenerating(true);
    try {
      const { data } = await api.post('/driver-share/me/code/regenerate');
      setCode(data.share_code);
      setCodeGeneratedAt(data.share_code_generated_at);
      toast.success('New code generated');
    } catch (err) {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const act = async (link, verb) => {
    setActingLink(link.id + verb);
    try {
      await api.post(`/driver-share/me/links/${link.id}/${verb}`);
      toast.success(
        verb === 'approve' ? `Approved — ${link.linked_company_name || 'Company'} can now dispatch you`
          : verb === 'reject' ? 'Request rejected'
          : 'Access revoked'
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setActingLink(null);
    }
  };

  const pending = links.filter((l) => l.status === 'pending');
  const active = links.filter((l) => l.status === 'active');
  const history = links.filter((l) => !['pending', 'active'].includes(l.status));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="driver-share-code-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-400" />
            Work with more companies
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Share code block */}
            <Card className="bg-slate-950 border-slate-800">
              <CardContent className="pt-4 pb-4 space-y-3">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Your Share Code</div>
                <div className="flex items-center gap-2">
                  <div
                    data-testid="driver-my-share-code"
                    className="flex-1 font-mono text-xl tracking-widest text-blue-300 bg-slate-900 border border-slate-800 rounded px-3 py-2 text-center"
                  >
                    {code || '—'}
                  </div>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={copy}
                    className="border-slate-700"
                    title="Copy code"
                    data-testid="driver-share-code-copy"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={regenerate}
                    disabled={regenerating}
                    className="border-slate-700"
                    title="Regenerate code"
                    data-testid="driver-share-code-regenerate"
                  >
                    {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Give this code to any company you want to work with. They&apos;ll need your approval before they can dispatch you.
                </p>
              </CardContent>
            </Card>

            {/* Pending approvals */}
            {pending.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-300">
                  <AlertCircle className="h-4 w-4" />
                  Pending approval ({pending.length})
                </div>
                <div className="space-y-2">
                  {pending.map((link) => (
                    <Card key={link.id} className="bg-amber-950/10 border-amber-900/40" data-testid={`pending-link-${link.id}`}>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium">{link.linked_company_name || link.linked_company_id}</div>
                            <div className="text-xs text-slate-500">
                              Requested {new Date(link.invited_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => act(link, 'reject')}
                              disabled={actingLink === link.id + 'reject'}
                              className="border-slate-700 text-slate-300 h-8"
                              data-testid={`reject-link-${link.id}`}
                            >
                              {actingLink === link.id + 'reject' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldX className="h-3 w-3 mr-1" />}
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => act(link, 'approve')}
                              disabled={actingLink === link.id + 'approve'}
                              className="bg-emerald-600 hover:bg-emerald-500 h-8"
                              data-testid={`approve-link-${link.id}`}
                            >
                              {actingLink === link.id + 'approve' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3 mr-1" />}
                              Approve
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Active links */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-300">
                Companies you work with ({active.length})
              </div>
              {active.length === 0 ? (
                <Card className="bg-slate-950 border-slate-800">
                  <CardContent className="py-4 text-sm text-slate-500 text-center">
                    No companies yet — share your code to link up.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {active.map((link) => (
                    <Card key={link.id} className="bg-slate-950 border-slate-800" data-testid={`active-link-${link.id}`}>
                      <CardContent className="py-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">{link.linked_company_name || link.linked_company_id}</div>
                          <div className="text-xs text-slate-500">
                            Active since {link.approved_at ? new Date(link.approved_at).toLocaleDateString() : '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status="active" />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => act(link, 'revoke')}
                            disabled={actingLink === link.id + 'revoke'}
                            className="border-rose-800/50 text-rose-300 hover:bg-rose-900/30 h-8"
                            data-testid={`revoke-link-${link.id}`}
                          >
                            {actingLink === link.id + 'revoke' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Revoke'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* History (collapsed by length) */}
            {history.length > 0 && (
              <details className="text-sm text-slate-400">
                <summary className="cursor-pointer select-none hover:text-slate-200">
                  History ({history.length})
                </summary>
                <div className="mt-2 space-y-1 text-xs">
                  {history.map((link) => (
                    <div key={link.id} className="flex items-center justify-between px-2 py-1.5 bg-slate-950/50 rounded">
                      <span>{link.linked_company_name || link.linked_company_id}</span>
                      <StatusBadge status={link.status} />
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverShareCodePanel;
